import React, { useState } from 'react';
import './LoginModal.css';

function PasswordInput({
  id,
  value,
  onChange,
  disabled,
  autoComplete,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoComplete?: string;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="auth-modal__password-wrap">
      <input
        type="text"
        id={id}
        className={`auth-modal__input auth-modal__input--password${visible ? '' : ' auth-modal__input--password-masked'}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        disabled={disabled}
        autoComplete={autoComplete}
        spellCheck={false}
      />
      <button
        type="button"
        className="auth-modal__password-toggle"
        onClick={() => setVisible((prev) => !prev)}
        disabled={disabled}
        aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
        aria-pressed={visible}
      >
        <i className={`fas fa-${visible ? 'eye-slash' : 'eye'}`} aria-hidden="true" />
      </button>
    </div>
  );
}

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<boolean>;
  onRegister: (name: string, email: string, password: string) => Promise<{ user: any; session: any }>;
  onForgotPassword?: () => void;
  /** @deprecated Не используется: после регистрации модалка закрывается, редирект делает родитель при логине */
  redirectPath?: string;
}

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onLogin,
  onRegister,
  onForgotPassword,
}) => {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const showError = (message: string) => {
    setError(message);
    setSuccess('');
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    setError('');
  };

  const hideMessages = () => {
    setError('');
    setSuccess('');
  };

  const validatePassword = (password: string) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password)
    };
    return Object.values(requirements).every(req => req);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    hideMessages();

    try {
      if (isLogin) {
        const success = await onLogin(email, password);
        if (success) {
          showSuccess('Вход выполнен успешно!');
        } else {
          showError('Неверный email или пароль');
        }
      } else {
        if (!name.trim()) {
          showError('Пожалуйста, введите имя');
          return;
        }
        if (!validatePassword(password)) {
          showError('Пароль не соответствует требованиям');
          return;
        }
        if (password !== confirmPassword) {
          showError('Пароли не совпадают');
          return
        }
        const result = await onRegister(name.trim(), email, password);

        if (result && result.user) {
          setName('');
          setEmail('');
          setPassword('');
          setConfirmPassword('');
          hideMessages();
          onClose();
        } else {
          showError('Регистрация не завершена. Попробуйте еще раз.');
        }
      }
    } catch (error: any) {
      let errorText = 'Произошла ошибка';

      if (error.message.includes('User already registered') || error.message.includes('already been registered') || error.message.includes('already exists')) {
        errorText = 'Пользователь с таким email уже зарегистрирован';
      } else if (error.message.includes('Password should be at least')) {
        errorText = 'Пароль слишком короткий';
      } else if (error.message.includes('Invalid email')) {
        errorText = 'Неверный формат email';
      } else if (error.message.includes('Invalid login credentials')) {
        errorText = 'Неверный email или пароль';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorText = 'Ошибка сети. Проверьте подключение к интернету';
      } else if (error.message) {
        errorText = error.message;
      }

      showError(errorText);
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = () => {
    setIsLogin(!isLogin);
    hideMessages();
    setName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal__overlay" onClick={onClose}>
      <div className="auth-modal__dialog" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal__header">
          <h2 className="auth-modal__title">
            {isLogin ? 'Вход в систему' : 'Регистрация'}
          </h2>
          <button type="button" className="auth-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="auth-modal__body">
          <p className="auth-modal__subtitle">
            {isLogin
              ? 'Войдите в свой аккаунт для доступа к данным'
              : 'Создайте аккаунт для доступа к данным'
            }
          </p>

          {error && <div className="auth-modal__error">{error}</div>}
          {success && <div className="auth-modal__success">{success}</div>}

          <form onSubmit={handleSubmit} className="auth-modal__form">
            {!isLogin && (
              <div className="auth-modal__field">
                <label htmlFor="name" className="auth-modal__label">Имя</label>
                <input
                  type="text"
                  id="name"
                  className="auth-modal__input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            )}

            <div className="auth-modal__field">
              <label htmlFor="email" className="auth-modal__label">Email</label>
              <input
                type="email"
                id="email"
                className="auth-modal__input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="auth-modal__field">
              <label htmlFor="password" className="auth-modal__label">Пароль</label>
              <PasswordInput
                id="password"
                value={password}
                onChange={setPassword}
                disabled={loading}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
              />
              {!isLogin && (
                <div className="auth-modal__pwd-req">
                  <div className={`auth-modal__req ${password.length >= 8 ? 'auth-modal__req--valid' : 'auth-modal__req--invalid'}`}>
                    <i className={`fas fa-${password.length >= 8 ? 'check' : 'circle'}`}></i>
                    Минимум 8 символов
                  </div>
                  <div className={`auth-modal__req ${/[A-Z]/.test(password) ? 'auth-modal__req--valid' : 'auth-modal__req--invalid'}`}>
                    <i className={`fas fa-${/[A-Z]/.test(password) ? 'check' : 'circle'}`}></i>
                    Заглавная буква
                  </div>
                  <div className={`auth-modal__req ${/[a-z]/.test(password) ? 'auth-modal__req--valid' : 'auth-modal__req--invalid'}`}>
                    <i className={`fas fa-${/[a-z]/.test(password) ? 'check' : 'circle'}`}></i>
                    Строчная буква
                  </div>
                  <div className={`auth-modal__req ${/\d/.test(password) ? 'auth-modal__req--valid' : 'auth-modal__req--invalid'}`}>
                    <i className={`fas fa-${/\d/.test(password) ? 'check' : 'circle'}`}></i>
                    Цифра
                  </div>
                </div>
              )}
            </div>

            {!isLogin && (
              <div className="auth-modal__field">
                <label htmlFor="confirmPassword" className="auth-modal__label">Подтвердите пароль</label>
                <PasswordInput
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            )}

            <button
              type="submit"
              className="auth-modal__submit"
              disabled={loading}
            >
              {loading ? 'Загрузка...' : (isLogin ? 'Войти' : 'Зарегистрироваться')}
            </button>
          </form>

          <div className="auth-modal__links">
            <p>
              {isLogin ? 'Нет аккаунта?' : 'Уже есть аккаунт?'}{' '}
              <button
                type="button"
                className="auth-modal__link"
                onClick={handleModeChange}
                disabled={loading}
              >
                {isLogin ? 'Зарегистрироваться' : 'Войти'}
              </button>
            </p>
            {isLogin && onForgotPassword && (
              <p>
                <button
                  type="button"
                  className="auth-modal__link"
                  onClick={() => { onClose(); onForgotPassword(); }}
                  disabled={loading}
                >
                  Забыли пароль?
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
